// Chemin: C:\gestion-copro\src\pages\admin\CourrierFormPage.tsx
// Session 47 : suppression mode Email, contenu pré-rempli
// Session 59 : Ajout copropriétaires comme destinataires possibles
// Session 80 : Correction appel updateCourrier (2 arguments au lieu de 4)
// Session 83 : Le rédacteur ne vote pas - exclure du comptage votesRequis
// Session 93 : Prefill depuis SENTINELLE (routage action generate_courrier)

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';
import { 
  createCourrier, 
  getCourrierById, 
  updateCourrier 
} from '../../services/courrier.service';
import { getInterlocuteurs } from '../../services/interlocuteur.service';
import { getAffairesByCopropriete } from '../../services/affaire.service';
import { getMembresCSByCopropriete } from '../../services/conseilSyndical.service';
import { getCoproprietaires } from '../../services/coproprietaire.service';
import { 
  Courrier,
  CourrierCreateData,
  CourrierUpdateData,
  TypeCourrier, 
  ModeEnvoi,
  DestinataireCourrier,
  TYPE_COURRIER_LABELS, 
  MODE_ENVOI_LABELS 
} from '../../types/courrier.types';
import { InterlocuteurAvecContactPrincipal } from '../../types/interlocuteur.types';
import { Affaire, STATUT_AFFAIRE_LABELS } from '../../types/affaire.types';
import { Coproprietaire } from '../../types/coproprietaire.types';
import { MembreCS } from '../../types/conseilSyndical.types';

// Types du formulaire
interface FormData {
  objet: string;
  contenu: string;
  type: TypeCourrier;
  modeEnvoi: ModeEnvoi;
  destinataireType: 'interlocuteur' | 'coproprietaire' | 'manuel';
  interlocuteurId: string;
  coproprietaireId: string;
  destinataireNom: string;
  destinataireEmail: string;
  destinataireAdresse: string;
  destinataireCategorie: DestinataireCourrier['type'];
  affaireId: string;
}

// Session 47 : Contenu par défaut pré-rempli (plus un placeholder)
const CONTENU_DEFAUT = `Madame, Monsieur,

Par la présente, le conseil syndical de la copropriété souhaite...

Dans l'attente de votre retour, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.`;

// Session 47 : Mode d'envoi par défaut = lettre recommandée (plus email)
const initialFormData: FormData = {
  objet: '',
  contenu: CONTENU_DEFAUT,
  type: 'demande_information',
  modeEnvoi: 'lrar',  // Session 47 : changé de 'email' à 'lrar'
  destinataireType: 'interlocuteur',
  interlocuteurId: '',
  coproprietaireId: '',
  destinataireNom: '',
  destinataireEmail: '',
  destinataireAdresse: '',
  destinataireCategorie: 'autre',
  affaireId: ''
};

// Session 47 : Modes d'envoi postaux uniquement (sans email)
const MODES_ENVOI_POSTAUX: ModeEnvoi[] = ['postal', 'lrar', 'recommande_ar'];

const CourrierFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: courrierId } = useParams<{ id: string }>();
  const { coproprieteId } = useCopropriete();
  const { firebaseUser, userProfile } = useAuth();
  
  const isEdition = !!courrierId;

  // Session 93 : Récupérer le prefill SENTINELLE depuis location.state
  const sentinellePrefill = (location.state as any)?.fromSentinelle 
    ? (location.state as any)?.prefill 
    : null;
  
  // Construire le nom d'affichage de l'utilisateur
  const getUserDisplayName = (): string => {
    if (userProfile?.prenom && userProfile?.nom) {
      return `${userProfile.prenom} ${userProfile.nom}`.trim();
    }
    if (userProfile?.nom) {
      return userProfile.nom;
    }
    if (userProfile?.email) {
      return userProfile.email;
    }
    return 'Utilisateur';
  };
  
  // États
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [interlocuteurs, setInterlocuteurs] = useState<InterlocuteurAvecContactPrincipal[]>([]);
  const [coproprietaires, setCoproprietaires] = useState<Coproprietaire[]>([]);
  const [membresCSNoms, setMembresCSIds] = useState<Set<string>>(new Set());
  const [affaires, setAffaires] = useState<Affaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [votesRequis, setVotesRequis] = useState<number>(0);
  const [courrierOriginal, setCourrierOriginal] = useState<Courrier | null>(null);

  // Chargement initial
  useEffect(() => {
    const chargerDonnees = async () => {
      if (!coproprieteId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Charger les interlocuteurs
        const interlocuteursResult = await getInterlocuteurs(coproprieteId, true);
        setInterlocuteurs(interlocuteursResult);
        
        // Session 59 : Charger les copropriétaires
        const coproprietairesResult = await getCoproprietaires(coproprieteId);
        setCoproprietaires(coproprietairesResult);
        
        // Session 59 : Charger les membres CS pour identifier avec badge
        // On compare par nom+prénom car MembreCS n'a pas de lien direct vers Coproprietaire
        const membresCS = await getMembresCSByCopropriete(coproprieteId, true);
        const csNoms = new Set<string>();
        membresCS.forEach((membre: MembreCS) => {
          const nomComplet = `${membre.prenom} ${membre.nom}`.trim().toLowerCase();
          csNoms.add(nomComplet);
        });
        setMembresCSIds(csNoms);
        
        // Charger les affaires
        const affairesResult = await getAffairesByCopropriete(coproprieteId);
        // Afficher toutes les affaires (pas de filtre sur le statut)
        setAffaires(affairesResult);
        
        // Charger le nombre de membres CS (pour votesRequis)
        // Session 83 : Exclure le rédacteur (utilisateur connecté) du comptage
        // Le rédacteur ne vote pas sur son propre courrier
        const membresActifs = membresCS.filter((m: MembreCS) => m.actif !== false);
        const membresVotants = membresActifs.filter((m: MembreCS) => 
          m.userId !== firebaseUser?.uid
        );
        setVotesRequis(membresVotants.length > 0 ? membresVotants.length : 1);
        
        // Si édition, charger le courrier existant
        if (courrierId) {
          const courrier = await getCourrierById(courrierId);
          if (!courrier) {
            setError('Courrier non trouvé');
            return;
          }
          
          if (courrier.statut !== 'brouillon') {
            setError('Ce courrier ne peut plus être modifié');
            return;
          }
          
          setCourrierOriginal(courrier);
          
          // Déterminer si le destinataire vient d'un interlocuteur
          const interlocuteurTrouve = interlocuteursResult.find(
            (i: InterlocuteurAvecContactPrincipal) => i.raisonSociale === courrier.destinataire.nom
          );
          
          // Déterminer si le destinataire vient d'un copropriétaire
          const coproprietaireTrouve = coproprietairesResult.find(
            (c: Coproprietaire) => `${c.prenom || ''} ${c.nom}`.trim() === courrier.destinataire.nom
          );
          
          let destinataireType: 'interlocuteur' | 'coproprietaire' | 'manuel' = 'manuel';
          if (interlocuteurTrouve) {
            destinataireType = 'interlocuteur';
          } else if (coproprietaireTrouve) {
            destinataireType = 'coproprietaire';
          }
          
          setFormData({
            objet: courrier.objet,
            contenu: courrier.contenu,
            type: courrier.type,
            modeEnvoi: courrier.modeEnvoi,
            destinataireType,
            interlocuteurId: interlocuteurTrouve?.id || '',
            coproprietaireId: coproprietaireTrouve?.id || '',
            destinataireNom: courrier.destinataire.nom,
            destinataireEmail: courrier.destinataire.email || '',
            destinataireAdresse: courrier.destinataire.adresse || '',
            destinataireCategorie: courrier.destinataire.type,
            affaireId: courrier.affaireId || ''
          });
        }
      } catch (err) {
        console.error('Erreur chargement données:', err);
        setError('Impossible de charger les données');
      } finally {
        setLoading(false);
      }
    };
    
    chargerDonnees();
  }, [coproprieteId, courrierId]);

  // Session 93 : Prefill SENTINELLE — appliquer les données pré-remplies après chargement
  useEffect(() => {
    if (!sentinellePrefill || loading || isEdition) return;

    setFormData(prev => {
      // Déterminer les champs destinataire si SENTINELLE en a fourni un
      let destinataireFields: Partial<FormData> = {};
      
      if (sentinellePrefill.destinataire) {
        const match = interlocuteurs.find(
          i => i.raisonSociale?.toLowerCase().includes(sentinellePrefill.destinataire.toLowerCase())
        );
        
        if (match) {
          // Convertir l'adresse objet en string si nécessaire
          let adresseStr = '';
          if (match.adresse) {
            if (typeof match.adresse === 'string') {
              adresseStr = match.adresse;
            } else {
              const a = match.adresse as { rue?: string; codePostal?: string; ville?: string };
              adresseStr = [a.rue, a.codePostal, a.ville].filter(Boolean).join(', ');
            }
          }
          
          destinataireFields = {
            destinataireType: 'interlocuteur',
            interlocuteurId: match.id,
            destinataireNom: match.raisonSociale || '',
            destinataireEmail: match.contactPrincipal?.email || '',
            destinataireAdresse: adresseStr,
            destinataireCategorie: (match.type as FormData['destinataireCategorie']) || 'autre',
          };
        } else {
          destinataireFields = {
            destinataireType: 'manuel',
            destinataireNom: sentinellePrefill.destinataire,
          };
        }
      }

      return {
        ...prev,
        objet: sentinellePrefill.objet || prev.objet,
        contenu: sentinellePrefill.contenu || prev.contenu,
        type: (sentinellePrefill.type as TypeCourrier) || prev.type,
        affaireId: sentinellePrefill.affaireId || prev.affaireId,
        ...destinataireFields,
      };
    });

    // Nettoyer le state de navigation pour éviter un re-prefill si on revient
    window.history.replaceState({}, document.title);
  }, [sentinellePrefill, loading, isEdition, interlocuteurs]);

  // Mise à jour du formulaire
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Session 59 : Sélection combinée (interlocuteur ou copropriétaire)
  const handleDestinataireSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    if (!value) {
      // Reset
      setFormData(prev => ({
        ...prev,
        interlocuteurId: '',
        coproprietaireId: '',
        destinataireNom: '',
        destinataireEmail: '',
        destinataireAdresse: '',
        destinataireCategorie: 'autre'
      }));
      return;
    }
    
    const [type, id] = value.split(':');
    
    if (type === 'interlocuteur') {
      const interlocuteur = interlocuteurs.find(i => i.id === id);
      if (interlocuteur) {
        const contact = interlocuteur.contactPrincipal;
        setFormData(prev => ({
          ...prev,
          interlocuteurId: id,
          coproprietaireId: '',
          destinataireNom: interlocuteur.raisonSociale,
          destinataireEmail: contact?.email || '',
          destinataireAdresse: interlocuteur.adresse 
            ? `${interlocuteur.adresse.rue}, ${interlocuteur.adresse.codePostal} ${interlocuteur.adresse.ville}`
            : '',
          destinataireCategorie: interlocuteur.type as DestinataireCourrier['type']
        }));
      }
    } else if (type === 'coproprietaire') {
      const copro = coproprietaires.find(c => c.id === id);
      if (copro) {
        setFormData(prev => ({
          ...prev,
          interlocuteurId: '',
          coproprietaireId: id,
          destinataireNom: `${copro.prenom || ''} ${copro.nom}`.trim(),
          destinataireEmail: copro.email || '',
          destinataireAdresse: copro.adresseCorrespondance || '',
          destinataireCategorie: 'coproprietaire'
        }));
      }
    }
  };

  // Changement de type de destinataire
  const handleDestinataireTypeChange = (type: 'interlocuteur' | 'coproprietaire' | 'manuel') => {
    setFormData(prev => ({
      ...prev,
      destinataireType: type,
      interlocuteurId: '',
      coproprietaireId: '',
      destinataireNom: '',
      destinataireEmail: '',
      destinataireAdresse: '',
      destinataireCategorie: type === 'coproprietaire' ? 'coproprietaire' : 'autre'
    }));
  };

  // Construire l'objet destinataire (sans champs undefined pour Firestore)
  const buildDestinataire = (): DestinataireCourrier => {
    const destinataire: DestinataireCourrier = {
      type: formData.destinataireCategorie,
      nom: formData.destinataireNom
    };
    
    if (formData.destinataireType === 'interlocuteur' && formData.interlocuteurId) {
      destinataire.interlocuteurId = formData.interlocuteurId;
    }
    if (formData.destinataireEmail.trim()) {
      destinataire.email = formData.destinataireEmail;
    }
    if (formData.destinataireAdresse.trim()) {
      destinataire.adresse = formData.destinataireAdresse;
    }
    
    return destinataire;
  };

  // Validation du formulaire
  const validateForm = (): string | null => {
    if (!formData.objet.trim()) {
      return 'L\'objet est obligatoire';
    }
    if (!formData.contenu.trim()) {
      return 'Le contenu est obligatoire';
    }
    if (!formData.destinataireNom.trim()) {
      return 'Le destinataire est obligatoire';
    }
    // Session 47 : L'adresse postale est obligatoire pour tous les modes (plus d'email)
    if (!formData.destinataireAdresse.trim()) {
      return 'L\'adresse postale du destinataire est obligatoire';
    }
    return null;
  };

  // Soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    if (!coproprieteId || !firebaseUser || !userProfile) {
      setError('Session invalide');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    const displayName = getUserDisplayName();
    
    try {
      const destinataire = buildDestinataire();
      
      if (isEdition && courrierId) {
        // Mise à jour d'un brouillon existant
        const updateData: CourrierUpdateData = {
          objet: formData.objet,
          contenu: formData.contenu,
          type: formData.type,
          modeEnvoi: formData.modeEnvoi,
          destinataire
        };
        
        // Ajouter affaireId seulement si renseigné
        if (formData.affaireId) {
          updateData.affaireId = formData.affaireId;
        }
        
        await updateCourrier(
          courrierId,
          updateData
        );
      } else {
        // Création d'un nouveau brouillon
        const createData: CourrierCreateData = {
          coproprieteId,
          objet: formData.objet,
          contenu: formData.contenu,
          type: formData.type,
          modeEnvoi: formData.modeEnvoi,
          destinataire,
          redacteurId: firebaseUser.uid,
          redacteurNom: displayName
        };
        
        // Ajouter affaireId seulement si renseigné
        if (formData.affaireId) {
          createData.affaireId = formData.affaireId;
        }

        // Session 216 : Rattachement au courrier d'origine pour calcul niveau relance (ESCALATOR CS)
        if (sentinellePrefill?.courrierId_lie) {
          createData.courrierReponseA = sentinellePrefill.courrierId_lie;
        }
        
        await createCourrier(createData, votesRequis);
      }
      
      navigate('/admin/courriers');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Impossible de sauvegarder le courrier');
    } finally {
      setSaving(false);
    }
  };

  // Annulation
  const handleCancel = () => {
    navigate('/admin/courriers');
  };

  // Valeur actuelle du sélecteur combiné
  const getSelectedValue = (): string => {
    if (formData.interlocuteurId) {
      return `interlocuteur:${formData.interlocuteurId}`;
    }
    if (formData.coproprietaireId) {
      return `coproprietaire:${formData.coproprietaireId}`;
    }
    return '';
  };

  // Affichage chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdition ? 'Modifier le courrier' : 'Nouveau courrier'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {isEdition 
            ? 'Modifiez le brouillon avant de le soumettre à validation.'
            : 'Rédigez un courrier qui sera soumis à l\'approbation du conseil syndical.'
          }
        </p>
      </div>

      {/* Session 93 : Bandeau SENTINELLE si pré-rempli */}
      {sentinellePrefill && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 text-sm font-bold">S</span>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-800">
              Pré-rempli par SENTINELLE
            </p>
            <p className="text-xs text-blue-600">
              Vérifiez et ajustez le contenu avant de soumettre. SENTINELLE propose, vous validez.
            </p>
          </div>
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section Destinataire */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Destinataire</h2>
          
          {/* Choix du type de sélection */}
          <div className="flex flex-wrap gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="destinataireTypeRadio"
                checked={formData.destinataireType === 'interlocuteur' || formData.destinataireType === 'coproprietaire'}
                onChange={() => handleDestinataireTypeChange('interlocuteur')}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">Sélectionner dans le carnet d'adresses</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="destinataireTypeRadio"
                checked={formData.destinataireType === 'manuel'}
                onChange={() => handleDestinataireTypeChange('manuel')}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">Saisie manuelle</span>
            </label>
          </div>

          {/* Session 59 : Sélecteur combiné avec optgroup */}
          {(formData.destinataireType === 'interlocuteur' || formData.destinataireType === 'coproprietaire') && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destinataire
              </label>
              <select
                value={getSelectedValue()}
                onChange={handleDestinataireSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Sélectionner --</option>
                
                {/* Groupe Interlocuteurs */}
                {interlocuteurs.length > 0 && (
                  <optgroup label="📁 INTERLOCUTEURS EXTERNES">
                    {interlocuteurs.map((interlocuteur) => (
                      <option key={`interlocuteur:${interlocuteur.id}`} value={`interlocuteur:${interlocuteur.id}`}>
                        {interlocuteur.raisonSociale} ({interlocuteur.type})
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {/* Groupe Copropriétaires - Triés par nom */}
                {coproprietaires.length > 0 && (
                  <optgroup label="👥 COPROPRIÉTAIRES">
                    {[...coproprietaires]
                      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
                      .map((copro) => {
                      const nomComplet = `${copro.prenom || ''} ${copro.nom}`.trim();
                      const isCS = membresCSNoms.has(nomComplet.toLowerCase());
                      // Affichage NOM Prénom pour meilleure lisibilité
                      const displayName = `${copro.nom} ${copro.prenom || ''}`.trim();
                      return (
                        <option key={`coproprietaire:${copro.id}`} value={`coproprietaire:${copro.id}`}>
                          {displayName}{isCS ? ' ★ CS' : ''}
                        </option>
                      );
                    })}
                  </optgroup>
                )}
              </select>
            </div>
          )}

          {/* Champs destinataire */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom / Raison sociale *
              </label>
              <input
                type="text"
                name="destinataireNom"
                value={formData.destinataireNom}
                onChange={handleChange}
                disabled={formData.destinataireType !== 'manuel' && (!!formData.interlocuteurId || !!formData.coproprietaireId)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Ex: Square Habitat"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie
              </label>
              <select
                name="destinataireCategorie"
                value={formData.destinataireCategorie}
                onChange={handleChange}
                disabled={formData.destinataireType !== 'manuel' && (!!formData.interlocuteurId || !!formData.coproprietaireId)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="syndic">Syndic</option>
                <option value="promoteur">Promoteur</option>
                <option value="entreprise">Entreprise</option>
                <option value="coproprietaire">Copropriétaire</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <input
                type="email"
                name="destinataireEmail"
                value={formData.destinataireEmail}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="contact@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse postale *
              </label>
              <input
                type="text"
                name="destinataireAdresse"
                value={formData.destinataireAdresse}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="123 rue Example, 59000 Lille"
              />
            </div>
          </div>
        </div>

        {/* Section Courrier */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contenu du courrier</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de courrier
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(TYPE_COURRIER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mode d'envoi
              </label>
              <select
                name="modeEnvoi"
                value={formData.modeEnvoi}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {/* Session 47 : Afficher uniquement les modes postaux (sans email) */}
                {MODES_ENVOI_POSTAUX.map((mode) => (
                  <option key={mode} value={mode}>{MODE_ENVOI_LABELS[mode]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Rattachement à une affaire (optionnel) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rattacher à une affaire
              <span className="ml-1 text-gray-400 font-normal">(optionnel)</span>
            </label>
            <select
              name="affaireId"
              value={formData.affaireId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Aucune affaire --</option>
              {affaires.map((affaire) => (
                <option key={affaire.id} value={affaire.id}>
                  {affaire.titre} ({STATUT_AFFAIRE_LABELS[affaire.statut] || affaire.statut})
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Le rattachement permet de retrouver ce courrier depuis le dossier de l'affaire.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Objet *
            </label>
            <input
              type="text"
              name="objet"
              value={formData.objet}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Demande de levée des réserves - Résidence L'ORION"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contenu *
            </label>
            <textarea
              name="contenu"
              value={formData.contenu}
              onChange={handleChange}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Rédigez le corps du courrier. La signature collective du conseil syndical sera ajoutée automatiquement.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {isEdition ? 'Enregistrer les modifications' : 'Créer le brouillon'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CourrierFormPage;
